import { CHECK_TYPE, CheckResultGeneric, GENERIC_RESULT_STATE, IndividualMultiTestPayload } from '@solid/shared';
import { saveAsCSV } from '@solid/shared/services/data-grid.service';
import { Button } from '@solid/shared/ui';
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckResultResponse } from '../../../services/check.service';
import Layout from '../../global/Layout';
import * as Styled from './CheckReportPageMultiTest.styles';
import { useTranslation } from 'react-i18next';

interface CheckReportPageMultiTestProps {
  checkId: string;
  context: 'personal' | 'org';
  check: CheckResultResponse;
}

const CheckReportPageMultiTest: React.FC<CheckReportPageMultiTestProps> = ({ check, checkId, context }) => {
  const result = (check?.result as IndividualMultiTestPayload)?.results ?? [];
  const { t } = useTranslation();
  const amountAddresses = result.length;
  const amountMatches = result.filter(r => r.hasMatches).length;
  const amountHits = result.filter(r => r.isHit).length;

  const getReportLink = (singleResult: IndividualMultiTestPayload['results'][number]) => {
    const link = new URL('', window.location.origin);
    if (singleResult.childId) {
      link.pathname = `/checks/${singleResult.childId}/report`;
    } else {
      const prefillData = {
        itemName: singleResult.name,
        result: {
          result: singleResult.isHit ? GENERIC_RESULT_STATE.FAILED : GENERIC_RESULT_STATE.SUCCESS,
          text: '',
        } as CheckResultGeneric,
        type: CHECK_TYPE.INDIVIDUAL_TEST,
        note: singleResult.note,
        questions: [
          { question: t('common.name'), value: singleResult.name, label: 'name' },
          { question: t('common.street'), value: singleResult.street ? singleResult.street : '-', label: 'street' },
          { question: t('common.city'), value: singleResult.city ? singleResult.city : '-', label: 'city' },
          { question: t('common.zipCode'), value: singleResult.zipCode ? singleResult.zipCode : '-', label: 'zipCode' },
          { question: t('common.country'), value: singleResult.country ? singleResult.country : '-', label: 'country' },
        ],
        submittedAt: check.submittedAt,
        userName: check.userName ?? '',
      } satisfies CheckResultResponse;

      link.pathname = '/checks/prefill/report';
      link.searchParams.append('prefillData', window.btoa(encodeURIComponent(JSON.stringify(prefillData))));
    }

    link.searchParams.append('context', context);
    link.searchParams.append('parentId', checkId);

    return link.toString();
  };

  const exportAsCSV = () => {
    const headersArray = [
      t('common.name'),
      t('common.street'),
      t('common.city'),
      t('common.zipCode'),
      t('common.country'),
      t('common.result'),
      t('common.comment'),
    ];
    const rowsArray = result.map(r => [
      r.name,
      r.street ? r.street : '-',
      r.city ? r.city : '-',
      r.zipCode ? r.zipCode : '-',
      r.country ? r.country : '-',
      r.isHit ? 'Sanktioniert' : 'Kein Treffer',
      r.note ? r.note : '-',
    ]);
    saveAsCSV(headersArray, rowsArray, 'check-report');
  };

  return (
    <Layout>
      <div className='wrapper'>
        <Styled.Headline>{t('checks.checkReportMultiTest.pageTitle')}</Styled.Headline>

        {check && (
          <Styled.StatisticContainer>
            <Styled.StatisticCard>
              <h4>{t('checks.checkReportMultiTest.numberOfChecks')}</h4>
              <Styled.StatisticCardValue>{amountAddresses}</Styled.StatisticCardValue>
            </Styled.StatisticCard>
            <Styled.StatisticCard>
              <h4>{t('checks.checkReportMultiTest.numberOfHits')}</h4>
              <Styled.StatisticCardValue>{amountMatches}</Styled.StatisticCardValue>
            </Styled.StatisticCard>
            <Styled.StatisticCard>
              <h4>{t('checks.checkReportMultiTest.numberOfSectioned')}</h4>
              <Styled.StatisticCardValue>{amountHits}</Styled.StatisticCardValue>
            </Styled.StatisticCard>
          </Styled.StatisticContainer>
        )}

        {check && (
          <Styled.ResultsTable>
            <thead>
              <tr>
                <th>{t('checks.checkReportMultiTest.name')}</th>
                <th>{t('checks.checkReportMultiTest.examination')}</th>
                <th>{t('checks.checkReportMultiTest.comment')}</th>
                <th>{t('checks.checkReportMultiTest.action')}</th>
              </tr>
            </thead>
            <tbody>
              {result.map((r, i) => (
                <tr key={i}>
                  <td>{r.name}</td>
                  <td>{r.isHit ? t('checks.checkReportMultiTest.sanctioned') : t('checks.checkReportMultiTest.notMatch')}</td>
                  <td>{r.note ? r.note : '-'}</td>
                  <td>
                    <Link to={getReportLink(r)}>
                      <Button>{t('checks.checkEntry.protocol')}</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Styled.ResultsTable>
        )}

        <Styled.BtnWrapper>
          <Link to={context === 'org' ? '/org/checks' : '/checks'}>
            <Button themeColor='primary' variant='outlined'>
              {t('pages.checkReport.backToCheckArchive')}
            </Button>
          </Link>
          <Button onClick={() => exportAsCSV()}>CSV Export</Button>
        </Styled.BtnWrapper>
      </div>
    </Layout>
  );
};

export default CheckReportPageMultiTest;
